<template>
  <div class="item-list">
    <v-dropdown
      id="add-dropdown"
      :disable-icon="true"
      button-classes="btn-new-item fw-bold justify-content-center mt-5"
      class="me-md-24"
      dropdown-classes="add-dropdown dropdown-menu-end"
    >
      <template #button>
        <v-icon icon="plus" space="me-8" size="sm" class="text-white" />
        <span class="btn-text">{{ $t("general.button.new") }}</span>
      </template>

      <template #content>
        <li v-for="(item, index) in sortedDropdownItems" :key="item.id" class="dropdown-list p-0" :class="{ 'border-bottom': index !== sortedDropdownItems.length - 1 }">
          <template v-if="item.offCanvasId">
            <a
            :id="item.id"
            :aria-label="item.label"
            class="dropdown-item d-flex align-items-center px-16 py-4"
            href="#"
            @click.prevent="
              $emit('emitCloseOffCanvas');
              openOffCanvas(item.offCanvasId);
            "
          >
            <v-icon class="text-secondary" :icon="item.icon" size="sm" space="me-12" />
            <span>{{ item.label }}</span>
          </a>
          </template>
          <template v-else>
            <a
            :id="item.id"
            :aria-label="item.label"
            class="dropdown-item d-flex align-items-center px-16 py-4"
            href="#"
            @click.prevent="caseCreateWizardOpened = true"
          >
            <v-icon class="text-secondary" :icon="item.icon" size="sm" space="me-12" />
            <span>{{ item.label }}</span>
          </a>
          </template>
        </li>
      </template>
    </v-dropdown>

    <the-admin-user-create-offcanvas
      v-if="offCanvasToShow === 'the-admin-user-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-users="$emit('reloadUsers')"
      @open-details="openDetailsOffCanvas('the-admin-user-details-offcanvas', $event)"
    />

    <the-admin-user-details-offcanvas
      v-if="offCanvasToShow === 'the-admin-user-details-offcanvas'"
      :is-archived="false"
      :user-data="currentData"
      @closed="
        closeOffCanvas();
        currentData = null;
      "
      @reload-users="$emit('reloadUsers')"
    />

    <the-admin-agency-create-offcanvas
      v-if="offCanvasToShow === 'the-admin-agency-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-agencies="$emit('reloadAgencies')"
      @open-details="openDetailsOffCanvas('the-admin-agency-details-offcanvas', $event)"
    />

    <the-admin-collaborator-create-offcanvas
      v-if="offCanvasToShow === 'the-admin-collaborator-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-agencies="$emit('reloadAgencies')"
      @open-details="openDetailsOffCanvas('the-admin-collaborator-details-offcanvas', $event)"
    />

    <the-admin-agency-details-offcanvas
      v-if="offCanvasToShow === 'the-admin-agency-details-offcanvas'"
      :agency-data="currentData"
      :is-archived="false"
      @closed="
        closeOffCanvas();
        currentData = null;
      "
      @reload-agencies="$emit('reloadAgencies')"
    />

    <the-client-create-offcanvas
      v-if="offCanvasToShow === 'the-client-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-clients="$emit('reloadClients')"
      @open-details="openDetailsOffCanvas('the-client-details-offcanvas', $event)"
    />

    <the-client-details-offcanvas
      v-if="offCanvasToShow === 'the-client-details-offcanvas'"
      :client-data="currentData"
      :is-archived="false"
      @closed="
        closeOffCanvas();
        currentData = null;
      "
      @reload-clients="$emit('reloadClients')"
    />

    <the-resource-create-offcanvas
      v-if="offCanvasToShow === 'the-resource-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-resources="$emit('reloadResources')"
      @open-details="openDetailsOffCanvas('the-resource-details-offcanvas', $event)"
    />

    <the-resource-information-create-offcanvas
      v-if="offCanvasToShow === 'the-resource-information-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-resources="$emit('reloadResources')"
      @open-details="openDetailsOffCanvas('the-resource-details-offcanvas', $event)"
    />

    <the-resource-details-offcanvas
      v-if="offCanvasToShow === 'the-resource-details-offcanvas'"
      :is-archived="false"
      :resource-data="currentData"
      @closed="
        closeOffCanvas();
        currentData = null;
      "
      @reload-resources="$emit('reloadResources')"
    />

    <the-supplier-create-offcanvas
      v-if="offCanvasToShow === 'the-supplier-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-suppliers="$emit('reloadSuppliers')"
      @open-details="openDetailsOffCanvas('the-supplier-details-offcanvas', $event)"
    />

    <the-supplier-details-offcanvas
      v-if="offCanvasToShow === 'the-supplier-details-offcanvas'"
      :is-archived="false"
      :supplier-data="currentData"
      @closed="
        closeOffCanvas();
        currentData = null;
      "
      @reload-suppliers="$emit('reloadSuppliers')"
    />

    <the-case-create-offcanvas
      v-if="offCanvasToShow === 'the-case-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-client-tours="$emit('reloadClientTours')"
    />

    <the-template-create-offcanvas
      v-if="offCanvasToShow === 'the-template-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-client-tours="$emit('reloadClientTours')"
    />

    <the-template-create-offcanvas-v2
      v-if="offCanvasToShow === 'the-template-create-offcanvas-v2'"
      @closed="closeOffCanvas"
      @reload-client-tours="$emit('reloadClientTours')"
    />

    <TheCaseCreateWizard v-if="caseCreateWizardOpened" @closed="caseCreateWizardOpened = false" @reload-case="$emit('reloadCases')" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VDropdown from "@/components/vendor/basic/dropdown/VDropdown.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import TheAdminUserCreateOffcanvas from "@/components/admin/user/TheAdminUserCreateOffcanvas.vue";
import TheAdminAgencyCreateOffcanvas from "@/components/admin/agency/TheAdminAgencyCreateOffcanvas.vue";
import TheAdminCollaboratorCreateOffcanvas from "@/components/admin/collaborator/TheAdminCollaboratorCreateOffcanvas.vue";
import TheClientCreateOffcanvas from "@/components/client/TheClientCreateOffcanvas.vue";
import TheResourceCreateOffcanvas from "@/components/resource/TheResourceCreateOffcanvas.vue";
import TheSupplierCreateOffcanvas from "@/components/supplier/TheSupplierCreateOffcanvas.vue";
import { useAuthStore } from '../../stores/auth';
import TheCaseCreateOffcanvas from "@/components/case/TheCaseCreateOffcanvas.vue";
import TheResourceInformationCreateOffcanvas from "@/components/resource/TheResourceInformationCreateOffcanvas.vue";
import TheClientDetailsOffcanvas from "@/components/client/TheClientDetailsOffcanvas.vue";
import { mapState } from 'pinia';
import TheAdminAgencyDetailsOffcanvas from "@/components/admin/agency/TheAdminAgencyDetailsOffcanvas.vue";
import TheAdminUserDetailsOffcanvas from "@/components/admin/user/TheAdminUserDetailsOffcanvas.vue";
import TheResourceDetailsOffcanvas from "@/components/resource/TheResourceDetailsOffcanvas.vue";
import TheSupplierDetailsOffcanvas from "@/components/supplier/TheSupplierDetailsOffcanvas.vue";
import TheTemplateCreateOffcanvas from "@/components/template/TheTemplateCreateOffcanvas.vue";
import TheTemplateCreateOffcanvasV2 from "@/components/template/V2/TheTemplateCreateOffcanvasV2.vue"
import TheCaseCreateWizard from "@/components/case/V2/TheCaseCreateWizard.vue";
import useCapabilities from "@/helpers/capabilities";

export default defineComponent({
  name: "VCreateItemDropdown",
  components: {
    TheTemplateCreateOffcanvas,
    TheTemplateCreateOffcanvasV2,
    TheSupplierDetailsOffcanvas,
    TheResourceDetailsOffcanvas,
    TheAdminUserDetailsOffcanvas,
    TheAdminAgencyDetailsOffcanvas,
    TheClientDetailsOffcanvas,
    TheResourceInformationCreateOffcanvas,
    TheCaseCreateOffcanvas,
    TheSupplierCreateOffcanvas,
    TheResourceCreateOffcanvas,
    TheClientCreateOffcanvas,
    TheAdminAgencyCreateOffcanvas,
    TheAdminCollaboratorCreateOffcanvas,
    TheAdminUserCreateOffcanvas,
    VDropdown,
    VIcon,
    TheCaseCreateWizard
  },
  emits: [
    "openFiltersOffcanvas",
    "emitCloseOffCanvas",
    "reloadUsers",
    "reloadAgencies",
    "reloadClients",
    "reloadResources",
    "reloadSuppliers",
    "reloadClientTours",
    "reloadCases",
  ],
  setup() {
    return {
      ...useOffCanvasUtils(),
      ...useCapabilities(),
    };
  },
  data() {
    return {
      currentData: null as any,
      caseCreateWizardOpened: false as boolean,
      dropdownItems: [
        {
          id: "create-user",
          label: this.$t("general.shared.user"),
          icon: "group",
          offCanvasId: "the-admin-user-create-offcanvas",
          can: "user.store",
        },
        {
          id: "create-agency",
          label: this.$t("general.shared.agency"),
          icon: "shop",
          offCanvasId: "the-admin-agency-create-offcanvas",
          can: "agency.store",
        },
        {
          id: "create-collaborator",
          label: this.$t("general.shared.collaborator"),
          icon: "user",
          offCanvasId: "the-admin-collaborator-create-offcanvas",
          can: "agency.store",
        },
        {
          id: "create-client",
          label: this.$t("general.shared.client"),
          icon: "group",
          offCanvasId: "the-client-create-offcanvas",
        },
        {
          id: "create-resource",
          label: this.$t("general.shared.resource"),
          icon: "send-love",
          offCanvasId: "the-resource-create-offcanvas",
        },
        {
          id: "create-resource-information",
          label: this.$t("general.shared.information"),
          icon: "marker",
          offCanvasId: "the-resource-information-create-offcanvas",
        },
        {
          id: "create-supplier",
          label: this.$t("general.shared.supplier"),
          icon: "briefcase",
          offCanvasId: "the-supplier-create-offcanvas",
        },
        {
          id: "create-case",
          label: this.$t("general.shared.case"),
          icon: "compass",
          offCanvasId: "the-case-create-offcanvas",
        },
        {
          id: "create-case-v2",
          label: `${this.$t("general.shared.case")} v2`,
          icon: "compass",
          modalShow: "the-case-create-wizard",
        },
        {
          id: "create-template",
          label: this.$t("general.shared.product"),
          icon: "map",
          offCanvasId: "the-template-create-offcanvas",
        },
        {
          id: "create-template-v2",
          label: `${this.$t("general.shared.product")} v2`,
          icon: "map",
          offCanvasId: "the-template-create-offcanvas-v2",
        }
      ],
    };
  },
  computed: {
    ...mapState(useAuthStore, ['agency']),
    sortedDropdownItems() {
      return this.dropdownItems
        .filter((item) => !item.can || this.can(item.can))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  methods: {
    openDetailsOffCanvas(offCanvasName: string, id: number) {
      this.currentData = id;

      if (this.offCanvasToShow !== offCanvasName) {
        this.openOffCanvas(offCanvasName);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(.btn-new-item) {
  height: 32px !important;
  padding: 0 16px !important;
  border-radius: 4px !important;
  background-color: #000000 !important;
  color: #ffffff !important;
  border: none !important;
  display: flex !important;
  align-items: center !important;
  line-height: 1 !important;

  &:hover {
    background-color: #333333 !important;
  }

  .btn-text {
    font-size: 11px !important;
    color: #ffffff !important;
    text-transform: uppercase;
  }
}
</style>
